<script setup lang="ts">
import type { GuideGroup, GuideGroupDefinition, GuideTask } from '@/onboarding/types'
import useOnboardingStore from '@/store/modules/onboarding'
import { useI18n } from 'vue-i18n'

const onboardingStore = useOnboardingStore()
const { locale, t } = useI18n()
const groups = computed(() => onboardingStore.edition.groups)

const recommendedTaskId = computed(() => onboardingStore.visibleTasks
  .find(task => !onboardingStore.completedTaskIds.includes(task.id))
  ?.id || '')

function groupedTasks(group: GuideGroup) {
  return onboardingStore.visibleTasks.filter(task => task.group === group)
}

function useArchiveChinese() {
  return locale.value.toLowerCase() === 'zh-cn'
}

function groupLabel(group: GuideGroupDefinition) {
  return useArchiveChinese() ? group.label : t(`onboarding.groups.${group.id}`)
}

function groupDescription(group: GuideGroupDefinition) {
  return useArchiveChinese() ? group.description : t(`onboarding.groupDescriptions.${group.id}`)
}

function taskTitle(task: GuideTask) {
  return useArchiveChinese()
    ? task.title
    : t(task.titleKey, { edition: t(onboardingStore.edition.nameKey) })
}

function taskDescription(task: GuideTask) {
  return useArchiveChinese() ? task.description : t(task.descriptionKey)
}

function taskStatus(taskId: string) {
  return onboardingStore.taskStatus(taskId)
}

function taskHasDataStatus(task: GuideTask, status: string) {
  return task.steps.some(step => onboardingStore.stepDataStatuses[step.id] === status)
}

function statusLabel(taskId: string) {
  return t(`onboarding.status.${taskStatus(taskId)}`)
}

function taskStateIcon(taskId: string) {
  const status = taskStatus(taskId)
  if (status === 'completed') {
    return '✓'
  }
  if (status === 'deferred') {
    return '⌛'
  }
  if (status === 'actionable') {
    return '+'
  }
  return ['partial', 'paused', 'running'].includes(status) ? '!' : ''
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && onboardingStore.taskCenterVisible) {
    onboardingStore.closeTaskCenter()
  }
}

onMounted(() => window.addEventListener('keydown', handleKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', handleKeydown))
</script>

<template>
  <Teleport to="body">
    <aside
      class="onboarding-task-panel"
      :class="{ 'is-open': onboardingStore.taskCenterVisible }"
      :aria-hidden="!onboardingStore.taskCenterVisible"
      :aria-label="t('onboarding.taskCenter.title')"
    >
      <header class="onboarding-task-header">
        <div class="onboarding-task-heading">
          <span class="onboarding-eyebrow">{{ t(onboardingStore.edition.nameKey) }}</span>
          <h2>
            {{ onboardingStore.showAllTasks
              ? t('onboarding.taskCenter.allTitle')
              : t('onboarding.taskCenter.pathTitle') }}
          </h2>
        </div>
        <el-button
          class="onboarding-task-close"
          circle
          :aria-label="t('onboarding.taskCenter.close')"
          @click="onboardingStore.closeTaskCenter"
        >
          <FaIcon name="i-ph:caret-right-bold" />
        </el-button>
      </header>

      <div class="onboarding-task-progress" aria-hidden="true">
        <span :style="{ width: `${onboardingStore.progress}%` }" />
      </div>

      <p class="onboarding-task-summary">
        {{ t('onboarding.taskCenter.summary', {
          completed: onboardingStore.completedVisibleTaskCount,
          total: onboardingStore.visibleTasks.length,
          minutes: onboardingStore.estimatedMinutes,
        }) }}
      </p>

      <div class="onboarding-task-content">
        <section
          v-for="group in groups"
          v-show="groupedTasks(group.id).length"
          :key="group.id"
          class="onboarding-task-group"
        >
          <div class="onboarding-task-group-head">
            <strong>{{ groupLabel(group) }}</strong>
            <span>{{ groupDescription(group) }}</span>
          </div>

          <button
            v-for="task in groupedTasks(group.id)"
            :key="task.id"
            type="button"
            class="onboarding-task-item"
            :class="{
              'is-complete': taskStatus(task.id) === 'completed',
              'is-partial': ['partial', 'paused', 'running'].includes(taskStatus(task.id)),
              'is-deferred': taskStatus(task.id) === 'deferred',
              'is-actionable': taskStatus(task.id) === 'actionable'
                || taskHasDataStatus(task, 'empty-creatable'),
            }"
            @click="onboardingStore.startTask(task.id)"
          >
            <span class="onboarding-task-state" aria-hidden="true">
              {{ taskStateIcon(task.id) }}
            </span>
            <span class="onboarding-task-copy">
              <strong>
                {{ taskTitle(task) }}
                <el-tag v-if="task.id === recommendedTaskId" size="small" effect="light">
                  {{ t('onboarding.common.recommended') }}
                </el-tag>
              </strong>
              <small>{{ taskDescription(task) }}</small>
            </span>
            <span class="onboarding-task-meta">
              <span>{{ t('onboarding.common.minutes', { minutes: onboardingStore.taskEstimatedMinutes(task) }) }}</span>
              <b
                :class="{
                  'is-active': [
                    'running',
                    'paused',
                    'deferred',
                    'actionable',
                  ].includes(taskStatus(task.id)),
                }"
              >
                {{ statusLabel(task.id) }}
              </b>
            </span>
          </button>
        </section>
      </div>

      <footer class="onboarding-task-footer">
        <el-button @click="onboardingStore.toggleAllTasks">
          {{ onboardingStore.showAllTasks
            ? t('onboarding.taskCenter.backToPath')
            : t('onboarding.taskCenter.viewAll') }}
        </el-button>
        <el-button @click="onboardingStore.restartSetup">
          {{ t('onboarding.common.resetSetup') }}
        </el-button>
        <el-button @click="onboardingStore.closeTaskCenter">
          {{ t('onboarding.common.escExit') }}
        </el-button>
      </footer>
    </aside>
  </Teleport>
</template>

<style>
.onboarding-task-panel {
  position: fixed;
  top: 84px;
  right: 16px;
  bottom: 16px;
  z-index: 33500;
  display: flex;
  visibility: hidden;
  flex-direction: column;
  width: min(384px, calc(100vw - 32px));
  overflow: hidden;
  background: #fff;
  border: 1px solid #dbe6f6;
  border-radius: 18px;
  box-shadow: 0 22px 60px rgb(15 42 88 / 20%);
  opacity: 0;
  transform: translateX(calc(100% + 32px));
  transition: opacity 0.2s ease, visibility 0.2s ease, transform 0.22s ease;
}

.onboarding-task-panel.is-open {
  visibility: visible;
  opacity: 1;
  transform: translateX(0);
}

.onboarding-task-header {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 98px;
  padding: 18px 18px 14px;
  background: linear-gradient(160deg, #eff6ff, #fff 68%);
  border-bottom: 1px solid #dbe6f6;
}

html body .onboarding-task-panel .onboarding-task-header {
  padding: 10px var(--app-layer-padding-x) 8px !important;
}

.onboarding-task-heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.onboarding-task-heading .onboarding-eyebrow {
  font-size: 14px;
  line-height: 20px;
}

.onboarding-task-heading h2 {
  margin: 1px 0 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  color: #0f2a58;
}

.onboarding-task-summary {
  flex: 0 0 auto;
  padding: 8px 17px 0;
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--el-text-color-secondary);
}

.onboarding-task-progress {
  flex: 0 0 3px;
  height: 3px;
  overflow: hidden;
  background: var(--el-color-primary-light-8);
}

.onboarding-task-progress span {
  display: block;
  height: 100%;
  background: var(--el-color-primary);
  border-radius: 0 999px 999px 0;
  transition: width 0.25s ease;
}

.onboarding-task-content {
  flex: 1 1 auto;
  min-height: 0;
  padding: 8px 12px 20px;
  overflow-y: auto;
  background: #fff;
  scrollbar-gutter: stable;
  overscroll-behavior: contain;
}

.onboarding-task-group + .onboarding-task-group {
  margin-top: 18px;
}

.onboarding-task-group-head {
  display: flex;
  gap: 12px;
  align-items: baseline;
  justify-content: space-between;
  padding: 0 5px 8px;
}

.onboarding-task-group-head strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.onboarding-task-group-head span {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 11px;
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}

.onboarding-task-item {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 3px 10px;
  width: 100%;
  min-height: 96px;
  padding: 13px 12px;
  text-align: left;
  cursor: pointer;
  background: #fff;
  border: 1px solid #dbe6f6;
  border-radius: 12px;
  transition: border-color 0.16s ease, background-color 0.16s ease, transform 0.16s ease;
}

.onboarding-task-item + .onboarding-task-item {
  margin-top: 8px;
}

.onboarding-task-item:hover {
  background: #f8fbff;
  border-color: var(--el-color-primary-light-5);
  transform: translateY(-1px);
}

.onboarding-task-item.is-complete {
  background: #fbfcfe;
  border-color: #dbe6f6;
}

.onboarding-task-state {
  display: grid;
  grid-row: 1 / span 2;
  place-items: center;
  width: 22px;
  height: 22px;
  margin-top: 1px;
  font-size: 12px;
  color: #fff;
  background: #fff;
  border: 1px solid #cbd8e8;
  border-radius: 999px;
}

.onboarding-task-item.is-complete .onboarding-task-state {
  background: var(--el-color-success);
  border-color: var(--el-color-success);
}

.onboarding-task-item.is-partial .onboarding-task-state {
  color: var(--el-bg-color);
  background: var(--el-color-warning);
  border-color: var(--el-color-warning);
}

.onboarding-task-item.is-deferred .onboarding-task-state {
  color: var(--el-bg-color);
  background: var(--el-color-warning);
  border-color: var(--el-color-warning);
}

.onboarding-task-item.is-actionable .onboarding-task-state {
  color: var(--el-bg-color);
  background: var(--el-color-success);
  border-color: var(--el-color-success);
}

.onboarding-task-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.onboarding-task-copy strong {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: #17345e;
}

.onboarding-task-copy small {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  font-size: 11px;
  line-height: 17px;
  color: #64748b;
  -webkit-box-orient: vertical;
}

.onboarding-task-copy .el-tag {
  height: 20px;
  padding-inline: 6px;
  margin-left: 6px;
  font-size: 10px;
  vertical-align: middle;
}

.onboarding-task-meta {
  display: flex;
  grid-column: 2;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  margin-top: 5px;
}

.onboarding-task-meta span,
.onboarding-task-meta b {
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  color: var(--el-text-color-secondary);
}

.onboarding-task-meta b.is-active {
  font-weight: 500;
  color: var(--el-color-primary);
}

.onboarding-task-footer .el-button {
  flex: 1 1 0;
  white-space: nowrap;
}

@media (1025px <= width <= 1439px) {
  .onboarding-task-panel {
    width: min(360px, calc(100vw - 32px));
  }
}

@media (width <= 1024px) {
  .onboarding-task-panel {
    top: 76px;
    right: 12px;
    bottom: 12px;
    width: min(360px, calc(100vw - 24px));
    transform: translateX(calc(100% + 24px));
  }

  .onboarding-task-panel.is-open {
    transform: translateX(0);
  }
}

@media (width <= 767px) {
  .onboarding-task-panel {
    inset: max(8px, env(safe-area-inset-top)) max(8px, env(safe-area-inset-right)) max(8px, env(safe-area-inset-bottom)) max(8px, env(safe-area-inset-left));
    width: auto;
    border-radius: 14px;
  }

  .onboarding-task-group-head span {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .onboarding-task-panel,
  .onboarding-task-progress span,
  .onboarding-task-item {
    transition: none;
  }
}
</style>
